import { Download, Print, Sync } from "@mui/icons-material";
import { IconButton, Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { GridColDef } from "@mui/x-data-grid";
import { GridCardContent } from "components/atoms";
import { renderProgress } from "components/atoms/PercentProgressCell";
import { AppTable } from "components/molecules";
import {
  ColorHelper,
  DownloadHelper,
  StatusHelper,
  TableHelper,
  TimeHelper,
} from "helpers";
import { usePaginationRequest } from "hooks";
import {
  EProductionPlanType,
  IDownloadProductionPlanDetailRequest,
  IGetAllProductionPlansRequest,
  IProductionPlan,
} from "model";
import { Fragment, useEffect, useState } from "react";
import {
  DownloadActions,
  PrintActions,
  ProductionPlanActions,
} from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import { ProductionPlanApiService } from "services";
import { theme } from "themes";
import { EXPORT_FILE_NAME } from "utils/Const";
import { I18nTranslator, translations } from "utils/i18n";
import PrintPPDModal from "./PrintPPDModal";
import UpdateStatusModal from "./UpdateStatusModal";

interface IProductionPlanByProductLot {
  request: IGetAllProductionPlansRequest;
}

const ProductionPlanByManufacturingDate = (
  props: IProductionPlanByProductLot
) => {
  const dispatch = useAppDispatch();
  const [request, setRequest] = useState(props.request);
  const [refresh, setRefresh] = useState(false);
  const [openPrintPPDModal, setOpenPrintPPDModal] = useState(false);
  const [openConfirmUpdateStatus, setOpenConfirmUpdateStatus] = useState(false);

  const [currentProductionPlan, setCurrentProductionPlan] =
    useState<IProductionPlan>();
  const action = useAppSelector((state) => state.ProductionPlanReducer.action);
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const { loading, result } = usePaginationRequest<
    IGetAllProductionPlansRequest,
    IProductionPlan[]
  >(
    request,
    ProductionPlanApiService.getAllProductionPlanByManufacturingDate,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  useEffect(() => {
    setRequest((prev) => ({
      ...prev,
      ...props.request,
    }));
  }, [props.request]);

  const handleChangePage = (pageNumber: number) => {
    setRequest({
      ...request,
      pageNumber,
    });
  };

  const handleSearch = (searchText: string) => {
    setRequest({
      ...request,
      searchText,
    });
  };

  const _handlePageSizeChange = (pageSize: number) => {
    setRequest({ ...request, pageSize: pageSize });
  };

  const handleDownloadClick = (row: IProductionPlan) => {
    const params: IDownloadProductionPlanDetailRequest = {
      id: row.id,
      fileName: `${EXPORT_FILE_NAME.productionPlanDetail}_[${row.productLot}]`,
    };
    dispatch(DownloadActions.downloadProductionPlanDetail.request(params));
  };

  const handlePrintClick = (row: IProductionPlan) => {
    if (!openPrintPPDModal) {
      setOpenPrintPPDModal(true);
      setCurrentProductionPlan(row);
    }
  };

  const handleUpdateStatusClick = () => {
    setOpenConfirmUpdateStatus(true);
  };

  const onConfirmPrint = (printQty: number) => {
    dispatch(
      PrintActions.print.request({
        id: currentProductionPlan!.id!,
        printQty: printQty,
      })
    );
  };
  useEffect(() => {
    if (
      action.includes(ProductionPlanActions.importProductionPlan.successName) ||
      action.includes(ProductionPlanActions.importProductionPlan.failedName)
    ) {
      dispatch(ProductionPlanActions.clearReducer.request());
      setRefresh(true);
    }
  }, [action]);

  const COLUMN: GridColDef[] = [
    TableHelper.renderSTT(),

    TableHelper.createCell({
      field: "id",
      headerName: I18nTranslator(translations.receive.action),
      size: "small",
      align: "center",
      sortable: true,
      renderCell: ({ row }) => (
        <Stack direction={"row"} spacing={1}>
          <IconButton
            onClick={() => {
              handleDownloadClick(row);
            }}
          >
            <Download fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => {
              handlePrintClick(row);
            }}
          >
            <Print sx={{ color: theme.colors.primary.main }} fontSize="small" />
          </IconButton>
          <IconButton onClick={handleUpdateStatusClick}>
            <Sync sx={{ color: theme.colors.primary.main }} fontSize="small" />
          </IconButton>
        </Stack>
      ),
    }),
    TableHelper.createCell({
      field: "planDate",
      headerName: I18nTranslator(translations.receive.planDate),
      size: "small",
      renderCell: ({ value }) => (
        <Typography>{TimeHelper.formatDate(value)}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "device",
      headerName: I18nTranslator(translations.receive.device),
      size: "small",
    }),

    TableHelper.createCell({
      field: "productLot",
      headerName: I18nTranslator(translations.receive.productLot),
      size: "small",
    }),
    TableHelper.createCell({
      field: "productCode",
      headerName: I18nTranslator(translations.receive.productCode),
      size: "small",
    }),

    TableHelper.createCell({
      field: "productName",
      headerName: I18nTranslator(translations.receive.productName),
      size: "full",
    }),
    TableHelper.createCell({
      field: "packageQuantity",
      headerName: I18nTranslator(translations.receive.packagingQuantity),
      size: 200,
      align: "right",
    }),
    TableHelper.createCell({
      field: "scannedQty",
      headerName: I18nTranslator(translations.receive.scannedQuantity),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "scannedPallet",
      headerName: I18nTranslator(translations.receive.scannedPalletQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "scannedCarton",
      headerName: I18nTranslator(translations.receive.scannedCartonQuantity),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "prepareProgress",
      headerName: I18nTranslator(translations.receive.packagingProgress),
      size: "small",
      align: "left",
      sortable: true,
      type: "number",
      renderCell: (params) =>
        renderProgress({
          ...params,
          value:
            params.row.packageQuantity === 0
              ? 0
              : (
                  (params.row.scannedQty / params.row.packageQuantity) *
                  100
                ).toFixed(0),
        }),
    }),
    TableHelper.createCell({
      field: "status",
      headerName: I18nTranslator(translations.receive.status),
      size: 200,
      renderCell: ({ value }) => {
        return (
          <Typography
            style={{
              backgroundColor: ColorHelper.getProductionPlanColor(
                value as EProductionPlanType
              ),
              padding: "3px 16px",
              borderRadius: "3px",
              color: "white",
              fontWeight: "bold",
            }}
          >
            {StatusHelper.getProductionPlanStatusString(value)}
          </Typography>
        );
      },
    }),
    TableHelper.createCell({
      field: "lvl1Specs",
      headerName: I18nTranslator(translations.receive.carton1),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl2Specs",
      headerName: I18nTranslator(translations.receive.carton2),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl3Specs",
      headerName: I18nTranslator(translations.receive.carton3),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl4Specs",
      headerName: I18nTranslator(translations.receive.carton4),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "palletsNeed",
      headerName: I18nTranslator(translations.receive.palletQuantityNeed),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl1Need",
      headerName: I18nTranslator(translations.receive.carton1quantityNeed),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl2Need",
      headerName: I18nTranslator(translations.receive.carton2quantityNeed),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl3Need",
      headerName: I18nTranslator(translations.receive.carton3quantityNeed),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "lvl4Need",
      headerName: I18nTranslator(translations.receive.carton4quantityNeed),
      size: "small",
      align: "right",
    }),

    TableHelper.createCell({
      field: "packageSize",
      headerName: I18nTranslator(translations.receive.packageSize),
      size: "small",
      align: "right",
    }),
    TableHelper.createCell({
      field: "uom",
      headerName: I18nTranslator(translations.receive.uom),
      size: "small",
      align: "center",
    }),

    TableHelper.createCell({
      field: "poNumber",
      headerName: I18nTranslator(translations.receive.poNumber),
      size: "small",
    }),

    TableHelper.createCell({
      field: "manufacturingDate",
      headerName: I18nTranslator(translations.receive.manufacturingDate),
      size: "small",
      renderCell: ({ value }) => (
        <Typography>{TimeHelper.formatDate(value)}</Typography>
      ),
    }),
    TableHelper.createCell({
      field: "expiryDate",
      headerName: I18nTranslator(translations.receive.expiryDate),
      size: "small",
      renderCell: ({ value }) => (
        <Typography>{TimeHelper.formatDate(value)}</Typography>
      ),
    }),

    TableHelper.createCell({
      field: "note",
      headerName: I18nTranslator(translations.receive.note),
      size: "full",
    }),
    TableHelper.createCell({
      field: "productCodeOld",
      headerName: I18nTranslator(translations.receive.productCodeOld),
      size: "small",
    }),
  ];

  return (
    <Fragment>
      <AppTable<IProductionPlan>
        loading={loading}
        data={result}
        columns={COLUMN}
        onSearch={handleSearch}
        searchPlaceholder={I18nTranslator(
          translations.placeholder.searchReceiveDocument
        )}
        onChangePage={handleChangePage}
        hideHeader
        hideSearch
        defaultHeaderTable={220}
        removeActionColumn
        actionColumnProps={{ hideDelete: true, hideImport: true }}
        onPageSizeChange={_handlePageSizeChange}
      />
      <PrintPPDModal
        currentProductionPlan={currentProductionPlan}
        open={openPrintPPDModal}
        onConfirmPrint={onConfirmPrint}
        onClose={() => setOpenPrintPPDModal(false)}
      />
      <UpdateStatusModal
        open={openConfirmUpdateStatus}
        onConfirm={() => {}}
        onClose={() => setOpenConfirmUpdateStatus(false)}
      />
    </Fragment>
  );
};

export default ProductionPlanByManufacturingDate;
